// usePlaybackEngine.js
import * as VesselUtil from "@/utils/VesselUtil";

const RISK_TEXT_COLORS = {
  SAFE: "#0ED463",
  CAUTION: "#EFF335",
  DANGER: "#EB5757",
};

export default function usePlaybackEngine(
  objectId,
  points,
  updateFeature,
  sourceType,
  properties = {}
) {
  let prev = null;
  let next = null;
  let latestFeature = null;

  const sortedPoints = [...points].sort((a, b) => a.t - b.t);

  const findSurroundingPoints = t => {
    prev = null;
    next = null;
    for (let i = 0; i < sortedPoints.length - 1; i++) {
      if (sortedPoints[i].t <= t && t <= sortedPoints[i + 1].t) {
        prev = sortedPoints[i];
        next = sortedPoints[i + 1];
        break;
      }
    }
  };

  const interpolate = t => {
    if (!prev || !next) {
      prev = sortedPoints[0];
      next = sortedPoints[0];
    }

    const isSinglePoint = prev === next;
    const ratio = (t - prev.t) / (next.t - prev.t);
    const lon = isSinglePoint
      ? prev.lon
      : prev.lon + (next.lon - prev.lon) * ratio;
    const lat = isSinglePoint
      ? prev.lat
      : prev.lat + (next.lat - prev.lat) * ratio;

    const commonProps = {
      objectId,
      t: isSinglePoint ? prev.t : t,
      source: sourceType,
    };

    const getRiskIcon = (r, isTrack) => {
      if (r === 99 || (r >= 0 && r <= 2))
        return isTrack ? "rg-vessel-safe" : "rg-target-object-safe-default";
      if (r >= 3 && r <= 7)
        return isTrack ? "rg-vessel-caution" : "rg-target-object-caution-default";
      if (r >= 8 && r <= 10)
        return isTrack ? "rg-vessel-danger" : "rg-target-object-danger-default";
      return isTrack ? "rg-vessel-safe" : "rg-target-object-safe-default";
    };

    const getRiskColor = r => {
      if (r === 99 || (r >= 0 && r <= 2)) return RISK_TEXT_COLORS.SAFE;
      if (r >= 3 && r <= 7) return RISK_TEXT_COLORS.CAUTION;
      if (r >= 8 && r <= 10) return RISK_TEXT_COLORS.DANGER;
      return RISK_TEXT_COLORS.SAFE;
    };

    const getRiskObjectFlameColor = r => {
      if (r === 99 || (r >= 0 && r <= 2)) return "rg-target-vessel-safe-default";
      if (r >= 3 && r <= 7) return "rg-target-vessel-caution-default";
      if (r >= 8 && r <= 10) return "rg-target-vessel-danger-default";
      return "rg-target-vessel-safe-default";
    }

    const sourceSpecificProps =
      sourceType === "ais"
        ? {
            icon: VesselUtil.getSymbolIconNameLightWeight(
              parseInt(properties.category),
              prev.v,
              0
            ),
            sog: prev.v,
            cog: prev.h,
            name: properties.nameJp || properties.name,
            heading: prev.h,
            isMyVessel: properties.isMyVessel,
          }
        : {
            icon: getRiskIcon(
              prev.r,
              !(prev.k === "" || prev.k === "buoy" || prev.k === "floating")
            ),
            sog: prev.sog,
            cog: prev.cog,
            name:
              (prev.k === "" || prev.k === "buoy"|| prev.k === "floating")
                ? `Target\n${properties.name}`
                : `Track\n${properties.name}`,
            textColor: getRiskColor(prev.r),
            objectFlameColor: getRiskObjectFlameColor(prev.r),
            heading: prev.cog,
            isTrack: !(prev.k === "" || prev.k === "buoy" || prev.k === "floating"),
            lat: prev.lat,
            lon: prev.lon,
            kind: prev.k,
            cameraId: prev.cameraId,
            log: prev.log,
            imgUrl: prev.p && prev.p.length > 0 ? prev.p[0].p : undefined,
            confidence: prev.p && prev.p.length > 0 ? prev.p[0].co : undefined,
            shootingDate: prev.p && prev.p.length > 0 ? prev.p[0].t : undefined,
            r: prev.r,
          };
    return {
      type: "Feature",
      id: `${objectId}_${t}`,
      geometry: {
        type: "Point",
        coordinates: [lon, lat],
      },
      properties: {
        ...commonProps,
        ...sourceSpecificProps,
      },
    };
  };

  const update = t => {
    if (t < sortedPoints[0].t) {
      updateFeature(objectId, null);
      latestFeature = null;
      return;
    }

    findSurroundingPoints(t);
    const feature = interpolate(t);
    if (feature) {
      latestFeature = feature;
      updateFeature(objectId, feature);
    }
  };

  const seek = t => {
    update(t);
  };

  const getLatestFeature = () => latestFeature;

  return { update, seek, getLatestFeature };
}
